/* eslint-disable @typescript-eslint/no-explicit-any */
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import type { Genre } from '@shared/interfacres/genre';
import { inject } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import type { GetFilter, GetPagination } from '@shared/interfacres/request-related';
import { DEFAULT_PAGE_SIZE, USER_DAILY_TRACKS_DOWNLOADS_LIMIT } from '@shared/constants/basic';
import { Category } from '@shared/interfacres/category';
import { TopTracksResponse, TrackApiData, TrackData } from '@shared/interfacres/track';
import { LibraryService } from '@data-access/user/services/library.service';
import { decreaseCounter, increaseCounter, markAsListened } from '@shared/helpers/tracks.helper';
import { MatSnackBar } from '@angular/material/snack-bar';


interface GenresState {
  tracks: TrackData[];
  topTracks: Partial<TrackData>[];
  genres: Genre[];
  subgenres: Genre[];
  categories: Category[];
  searchGenreId: number | null;
  searchSubGenreId: number | null;
  searchCategoryId: number | null;
  total: number;
  name: string;
  isLoading: boolean;
  isTopLoading: boolean;
  isAttributesLoading: boolean;
  filter: GetFilter;
  pagination: GetPagination;
  downloadsLeft: number;
}

const initialState: GenresState = {

  tracks: [],
  topTracks: [],
  genres: [],
  subgenres: [],
  categories: [],
  total: 0,
  name: '',
  searchGenreId: null,
  searchSubGenreId: null,
  searchCategoryId: null,
  isLoading: false,
  isTopLoading: false,
  isAttributesLoading: false,
  filter: { query: '', order: 'desc' },
  pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
  downloadsLeft: USER_DAILY_TRACKS_DOWNLOADS_LIMIT,
};

export const LibraryStore = signalStore(
  withState(initialState),

  withMethods((store, service = inject(LibraryService), snackBar = inject(MatSnackBar)) => ({

    setName(name: string) {
      patchState(store, () => ({
        name,
        pagination: {
          pageSize: store.pagination.pageSize(),
          page: 1
        }
      }));
      return this.getFiltered();
    },

    setPagination(page: number, pageSize: number) {
      patchState(store, (state) => ({
        pagination: {
          ...state.pagination,
          page,
          pageSize,
        }
      }));
      return this.getFiltered();
    },

    setFilter(query: string, order: 'asc' | 'desc') {
      patchState(store, (state) => ({
        filter: {
          ...state.filter,
          query,
          order,
        }
      }));
      return this.getFiltered();
    },
    setGenreId(id: number| null) {
      if (store.searchGenreId() !== id) {
        patchState(store, () => ({
          searchGenreId: id,
          pagination: {
            pageSize: store.pagination.pageSize(),
            page: 1
          }
        }));
        this.getFiltered();
      }
    },
    setSubGenreId(id: number| null) {
      if (store.searchSubGenreId() !== id) {
        patchState(store, () => ({
          searchSubGenreId: id,
          pagination: {
            pageSize: store.pagination.pageSize(),
            page: 1
          }
        }));
       this.getFiltered();
      }

    },
    setCategoryId(id: number | null) {

      if (store.searchCategoryId() !== id) {
        patchState(store, () => ({
          searchCategoryId: id,
          pagination: {
            pageSize: store.pagination.pageSize(),
            page: 1
          }
        }));
        this.getFiltered();
      }

    },
    async getTopTracks() {
      patchState(store, () => ({
        isTopLoading: true,
      }));

      const items = await firstValueFrom(service.getTopTracks());
      patchState(store, () => ({
        isTopLoading: false,
        topTracks: items.map((item: TopTracksResponse) => {
          return {
            ...item,
            downloadsCount: item.downloads ? item.downloads[0]?.count : 0,
          }
        })
      }));

    },

    async getAttributesLists() {
      patchState(store, () => ({
        isAttributesLoading: true,
      }));

      const genresListReq = firstValueFrom(service.getGenresList());
      const subgenresListReq = firstValueFrom(service.getSubGenresList());
      const categoriesListReq = firstValueFrom(service.getCategoriesList());

      const [genres, subgenres, categories] = await Promise.all([genresListReq, subgenresListReq, categoriesListReq]);

      patchState(store, () => ({
        isAttributesLoading: false,
        categories,
        genres: genres.filter((item: Genre) => item.name.toLowerCase() !== 'unknown'),
        subgenres: subgenres.filter((item: Genre) => item.name.toLowerCase() !== 'unknown'),
      }));
    },

    async getFiltered() {
      patchState(store, () => ({
        isLoading: true,
      }));

      const name = store.name() || '';

      const attributes = {
        categoryId: store.searchCategoryId(),
        genreId: store.searchGenreId(),
        subgenreId: store.searchSubGenreId()
      }

      try {
        const withPagination = await firstValueFrom(service.getFilteredList(name, store.filter(), store.pagination(), attributes));
        patchState(store, () => ({
          isLoading: false,
          tracks: withPagination.data.map((track: TrackApiData) => {
            return {
              ...track,
              downloadsCount: track.download ? track.download[0]?.count : 0
            }
          }),
          total: withPagination.pagination.total
        }));
      } catch (e) {
        snackBar.open('Error', (e as { error : {message: string} }).error?.message, { duration: 20000, horizontalPosition: 'end', panelClass: 'error-toast' });
      } finally {
        patchState(store, () => ({
          isLoading: false,
        }));
      }


    },

    increaseTopTrackCounter(id: number) {
      patchState(store, (state) => ({
        topTracks: increaseCounter(id, state.topTracks)
      }))

    },

    increaseTrackCounter(id: number) {
      patchState(store, (state) => ({
        tracks: increaseCounter(id, state.tracks) as TrackData[]
      }))
    },

    decreaseTopTrackCounter(id: number) {
      patchState(store, (state) => ({
        topTracks: decreaseCounter(id, state.topTracks)
      }))

    },

    decreaseTrackCounter(id: number) {
      patchState(store, (state) => ({
        tracks: decreaseCounter(id, state.tracks) as TrackData[]
      }))
    },

    markTopTrackAsListened(id: number) {
      patchState(store, (state) => ({
        topTracks: markAsListened(id, state.topTracks)
      }));
    },

    markTrackAsListened(id: number) {
      patchState(store, (state) => ({
        tracks: markAsListened(id, state.tracks) as TrackData[]
      }));
      return service.markAsListened(id);
    },

    async getDownloadUrl(id: number) {
      try {
        const data: {success: boolean, downloadUrl: string, message: string, dailyDownloadsRemaining: number} = await firstValueFrom(service.getDownloadData(id));

        patchState(store, () => ({
          downloadsLeft: data.dailyDownloadsRemaining
        }))
        if (data.success) {

          return data.downloadUrl;
        }

        snackBar.open('Error', data.message, { duration: 20000, horizontalPosition: 'end', panelClass: 'error-toast' });

        return false;
      } catch (e: any) {
        console.log(e);
        patchState(store, () => ({
          downloadsLeft: e?.error?.dailyDownloadsRemaining || 0
        }))
        snackBar.open('Error', e?.error?.message, { duration: 20000, horizontalPosition: 'end', panelClass: 'error-toast' });
        return false;
      }

    },

    resetDailyDownloads() {
      patchState(store, () => ({
        downloadsLeft: USER_DAILY_TRACKS_DOWNLOADS_LIMIT
      }));
    },

    clearCriteria() {
      patchState(store, () => ({
        name: '',
        searchGenreId: null,
        searchSubGenreId: null,
        searchCategoryId: null,
        filter: { query: '', order: 'desc' } as GetFilter,
        pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
      }));
    },

  }))
);
